/**
 * ModuleListHelper - Centralized module list operations
 *
 * This helper provides unified logic for automatically adding eligible files
 * (.py, .js, .whl) to the Module List across all file sources and scenarios.
 *
 * Single Responsibility: Module List business logic
 */

import { firstValueFrom } from 'rxjs';
import { GeneralHelpers } from './general.helper';
import { FileService } from '../services/file/file.service';
import { FILE_TYPE } from '../constants/general.constants';
import { ProjectFile, NotebookFile } from '@app/shared/interfaces';

export class ModuleListHelper {

  /**
   * Auto-add file to module list if it's an eligible module type (.py, .js, .whl)
   * Unified method that replaces all scattered auto-add logic
   */
  static async autoAddFileToModulesIfEligible(
    file: ProjectFile | NotebookFile,
    fileService: FileService,
    data?: ArrayBuffer | string
  ): Promise<void> {
    try {
      // Use existing helper to determine file type
      const fileType = GeneralHelpers.getFileType(GeneralHelpers.fileExtensionFromString(file.name));

      // Check if file is a module type
      if ([FILE_TYPE.whl, FILE_TYPE.python, FILE_TYPE.javascript].includes(fileType)) {
        console.log(`🔄 ModuleListHelper: Auto-adding ${file.name} to module list (${fileType})`);

        // Delegate to FileService core method (reuse existing logic)
        await fileService.addToModuleList(file as ProjectFile, data);
      }
    } catch (error: any) {
      console.warn(`⚠️ ModuleListHelper: Failed to auto-add ${file.name}:`, error);
      // Non-blocking - don't throw to avoid breaking file operations
    }
  }

  /**
   * Sync all project files to module list for UI initialization
   * Ensures existing project files appear in Module List without script execution
   */
  static async syncProjectFilesToModules(
    projectId: number,
    versionId: number,
    fileService: FileService
  ): Promise<void> {
    if (!projectId || !versionId) {
      console.log('🔄 ModuleListHelper: Cannot sync - missing projectId or versionId');
      return;
    }

    try {
      console.log(`🔄 ModuleListHelper: Syncing project files for project ${projectId}`);

      // Get current project files (single source of truth)
      const projectFiles = await firstValueFrom(fileService.getProjectFiles(projectId, versionId));

      let addedCount = 0;
      for (const file of projectFiles) {
        // Check if file is eligible and not already in module list
        if (await this.shouldAddToModules(file, fileService)) {
          try {
            console.log(`📦 ModuleListHelper: Syncing ${file.name} to modules`);
            await this.autoAddFileToModulesIfEligible(file, fileService);
            addedCount++;
          } catch (error) {
            console.warn(`⚠️ ModuleListHelper: Failed to sync ${file.name}:`, error);
          }
        }
      }

      if (addedCount > 0) {
        console.log(`✅ ModuleListHelper: Synced ${addedCount} project files to module list`);
      } else {
        console.log('📋 ModuleListHelper: All eligible project files already in module list');
      }
    } catch (error) {
      console.warn('⚠️ ModuleListHelper: Failed to sync project files:', error);
    }
  }

  /**
   * Batch process multiple files for module list addition
   * Used for bulk operations like project file loading
   */
  static async batchAddFilesToModules(
    files: (ProjectFile | NotebookFile)[],
    fileService: FileService
  ): Promise<void> {
    console.log(`🔄 ModuleListHelper: Batch processing ${files.length} files for module list`);

    let addedCount = 0;
    for (const file of files) {
      try {
        // Check if file should be added before attempting
        if (await this.shouldAddToModules(file, fileService)) {
          await this.autoAddFileToModulesIfEligible(file, fileService, (file as any).content);
          addedCount++;
        }
      } catch (error) {
        console.warn(`⚠️ ModuleListHelper: Failed batch add for ${file.name}:`, error);
        // Continue with other files
      }
    }

    if (addedCount > 0) {
      console.log(`✅ ModuleListHelper: Batch added ${addedCount} files to modules`);
    }
  }

  // =============== Private helpers ===============

  /**
   * Check if file should be added to module list
   */
  private static async shouldAddToModules(file: ProjectFile | NotebookFile, fileService: FileService): Promise<boolean> {
    // Check file type eligibility
    const fileType = GeneralHelpers.getFileType(GeneralHelpers.fileExtensionFromString(file.name));
    if (![FILE_TYPE.whl, FILE_TYPE.python, FILE_TYPE.javascript].includes(fileType)) {
      return false;
    }

    // Check if already in module list to avoid duplicates
    // CRITICAL: Use currentProjectModuleList() (not moduleList()) to ensure
    // we only check modules for the CURRENT project, preventing cross-project false positives
    const existsInModules = fileService.currentProjectModuleList().some(m =>
      m.fileId === file.fileId
    );
    return !existsInModules;
  }
}